import type {
  SafersCCTVResponse,
  SafersCCTV,
  CctvPlaybackResponse,
  PtzPreset,
} from "./types/cctv.types";
import { ApiError } from "@safers/auth";
import { API_BASE_URL } from "./config";

/** 응답 형태 검증 — data 배열이 존재하는지 확인 */
function isCCTVResponse(value: unknown): value is SafersCCTVResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "data" in value &&
    Array.isArray((value as SafersCCTVResponse).data)
  );
}

/** CCTV 목록 조회 (siteId로 현장별 필터링 가능) */
async function getCCTVs(siteId?: number): Promise<SafersCCTV[]> {
  const query = siteId != null ? `?siteId=${siteId}` : "";
  const res = await fetch(`${API_BASE_URL}/cctvs${query}`);
  if (!res.ok) throw new Error(`Failed to fetch CCTVs: ${res.status}`);
  const body: unknown = await res.json();
  if (!isCCTVResponse(body)) throw new Error("Invalid CCTV response shape");
  return body.data;
}

/** PTZ continuous 이동 시작 — pan/tilt/zoom 각 -1.0~1.0 속도. 정지는 ptzStop */
async function ptzMove(
  id: number,
  body: { pan?: number; tilt?: number; zoom?: number }
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to move PTZ: ${res.status}`);
}

/** 진행 중인 continuous 이동(PanTilt·Zoom) 정지 */
async function ptzStop(id: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/stop`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to stop PTZ: ${res.status}`);
}

/** PTZ 프리셋 목록 */
async function ptzPresets(id: number): Promise<PtzPreset[]> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets`);
  if (!res.ok) throw new Error(`Failed to fetch presets: ${res.status}`);
  const body = (await res.json()) as { data?: PtzPreset[] };
  return Array.isArray(body.data) ? body.data : [];
}

/** 현재 위치를 프리셋으로 저장 */
async function ptzPresetSave(id: number, name: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Failed to save preset: ${res.status}`);
}

/** 프리셋 위치로 이동 */
async function ptzPresetGoto(id: number, token: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}/goto`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`Failed to goto preset: ${res.status}`);
}

/** 프리셋 삭제 */
async function ptzPresetDelete(id: number, token: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete preset: ${res.status}`);
}

/** 미디어서버에서 CCTV 경로 목록을 가져와 DB에 동기화 */
async function syncCCTVs(siteId?: number): Promise<void> {
  const query = siteId != null ? `?siteId=${siteId}` : "";
  const res = await fetch(`${API_BASE_URL}/cctvs/sync${query}`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to sync CCTVs: ${res.status}`);
}

/**
 * WHEP URL 생성 — `/webrtc/{streamName}/whep`.
 *
 * 미디어 서버는 단일 base로 운영(현장별 포트 분기 없음).
 * dev에서는 vite proxy(`/webrtc` → VITE_MEDIA_SERVER_URL)가 origin을 우회시키고,
 * prod에서는 같은 origin이라 그대로 fetch.
 *
 * streamName은 `encodeURIComponent`로 인코딩 — 공백·특수문자에 안전.
 */
function getWHEPUrl(streamName: string): string {
  return `/webrtc/${encodeURIComponent(streamName)}/whep`;
}

/** NVR 녹화영상 재생 세션 생성 — pathName을 받아 WHEP URL 구성에 사용 */
async function requestPlayback(
  cctvId: number,
  startDate: string,
  endDate: string
): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${cctvId}/playback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!res.ok) throw new Error(`Playback request failed: ${res.status}`);
  const body: CctvPlaybackResponse = await res.json();
  return body.data.pathName;
}

/**
 * 1회 다운로드 최대 구간(분). **서버 상한은 12시간이고, 이건 그보다 좁은 화면 상한이다.**
 *
 * 조각을 Blob 으로 모으므로 한 번에 모이는 양이 부담이 된다. 이어받기도 없어서 길게
 * 잡을수록 중간에 끊길 때 잃는 것이 커진다.
 */
const MAX_DOWNLOAD_MINUTES = 2 * 60;

/*
 * 아래 상수는 운영 실측(2026-09-03, NVR 7곳)에서 나왔다. 넉넉한 쪽으로 잡는다 —
 * 안내보다 빨리 끝나는 건 괜찮지만 오래 걸리면 사용자가 실패로 읽는다.
 */

/**
 * 영상 1초당 바이트의 **범위**.
 *
 * 응답에 Content-Length 가 없어 누르기 전에는 추정할 수밖에 없는데, 하나의 값으로는
 * 안 된다. 전부 1920x1080 인데도 현장별 인코더 설정 때문에 0.96~3.49 Mbps 로 벌어졌다.
 * 받는 중의 정확한 진행률은 `createFragmentClock` 이 스트림에서 직접 읽는다.
 */
const BYTES_PER_VIDEO_SECOND_MIN = 128 * 1024; // 1.0 Mbps
const BYTES_PER_VIDEO_SECOND_MAX = 448 * 1024; // 3.5 Mbps

/**
 * 한 번에 받을 수 있는 최대 바이트.
 *
 * 구간 상한 2시간은 1 Mbps 기준 약 900MB 다. 3.5 Mbps 현장에서는 같은 2시간이 3.1GB 라
 * Blob 으로 모을 수 없다. 비트레이트는 몇 초만 받아보면 알 수 있으므로, 그 값으로
 * 총량을 다시 내 이 선을 넘으면 일찍 끊는다.
 */
const MAX_DOWNLOAD_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * 전송 배속과 앞단 고정 지연(초).
 *
 * 실측은 탐색 11초 + 실시간의 18배였으나, 18배는 약 17Mbps 를 쓴다는 뜻이라 현장 회선이
 * 좁으면 거기서 먼저 막힌다. 낮춰 잡아 안내가 실제보다 길게 나오게 둔다.
 *
 * 용량과 달리 **시간은 비트레이트와 무관하다.** 1분 구간이 0.99 Mbps 에서 14.1초,
 * 3.49 Mbps 에서 15.0초였다 — NVR 이 실시간의 일정 배수로 읽어 보낸다.
 */
const DOWNLOAD_SPEEDUP = 12;
const DOWNLOAD_STARTUP_SECONDS = 15;

export interface DownloadEstimate {
  /** 저비트레이트 현장 기준 예상 용량 */
  minBytes: number;
  /** 고비트레이트 현장 기준 예상 용량 */
  maxBytes: number;
  seconds: number;
}

/** 구간 길이로 낸 추정치. 용량은 범위, 시간은 하나의 값이다. */
function estimateDownload(rangeMinutes: number): DownloadEstimate {
  const videoSeconds = rangeMinutes * 60;
  return {
    minBytes: videoSeconds * BYTES_PER_VIDEO_SECOND_MIN,
    maxBytes: videoSeconds * BYTES_PER_VIDEO_SECOND_MAX,
    seconds: DOWNLOAD_STARTUP_SECONDS + videoSeconds / DOWNLOAD_SPEEDUP,
  };
}

/** 녹화영상 다운로드 주소. */
function getDownloadUrl(cctvId: number, startDate: string, endDate: string): string {
  const query = new URLSearchParams({ startDate, endDate });
  return `${API_BASE_URL}/cctvs/${cctvId}/download?${query}`;
}

export interface DownloadProgress {
  /** 지금까지 받은 바이트 */
  receivedBytes: number;
  /**
   * 0~1 의 진행도. 스트림에서 읽어낸 **실제** 값이라 비트레이트와 무관하다.
   * 아직 첫 조각을 못 읽었거나 파싱이 실패하면 null — 그때는 진행률을 숨긴다.
   */
  ratio: number | null;
  /**
   * 실제 비트레이트로 다시 낸 총 용량. 받기 시작해야 알 수 있으므로 처음에는 null.
   * 누르기 전 범위 추정치와 달리 이건 이 카메라의 실제 값이다.
   */
  projectedBytes: number | null;
}

/**
 * 프래그먼트 MP4 를 흘려 읽으며 지금까지의 미디어 시간을 센다.
 *
 * 바이트로 진행률을 내면 분모를 모른다. 대신 스트림이 답을 들고 있다 —
 * 구조가 `ftyp moov (moof mdat)*` 이고 각 `moof` 의 `tfdt` 가 그 조각의 미디어 시각이다.
 * `mdat` 은 크기만큼 건너뛴다.
 */
function createFragmentClock() {
  // 타입을 넓혀 둔다 — subarray 는 ArrayBufferLike 를 돌려주고 new Uint8Array 는 ArrayBuffer 다.
  let buffer: Uint8Array = new Uint8Array(0);
  let skipRemaining = 0;
  let timescale: number | null = null;
  let mediaSeconds = 0;
  let broken = false;

  function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
  }

  /** 박스 안에서 `type` 을 찾아 그 payload 시작 위치를 준다. 없으면 -1. */
  function findBox(view: DataView, type: string): number {
    for (let i = 0; i + 8 <= view.byteLength; i++) {
      if (
        view.getUint8(i + 4) === type.charCodeAt(0) &&
        view.getUint8(i + 5) === type.charCodeAt(1) &&
        view.getUint8(i + 6) === type.charCodeAt(2) &&
        view.getUint8(i + 7) === type.charCodeAt(3)
      ) {
        return i + 8;
      }
    }
    return -1;
  }

  return {
    /** 예외를 밖으로 내보내지 않는다 — 진행률 때문에 다운로드가 죽으면 안 된다. */
    push(chunk: Uint8Array): void {
      if (broken) return;
      try {
        let rest = chunk;
        if (skipRemaining > 0) {
          const n = Math.min(skipRemaining, rest.length);
          skipRemaining -= n;
          rest = rest.subarray(n);
          if (rest.length === 0) return;
        }
        buffer = concat(buffer, rest);

        for (;;) {
          if (buffer.length < 8) return;
          const head = new DataView(buffer.buffer, buffer.byteOffset, 8);
          let size = head.getUint32(0);
          const type = String.fromCharCode(
            head.getUint8(4),
            head.getUint8(5),
            head.getUint8(6),
            head.getUint8(7)
          );
          // 64비트 크기. 여기서는 mdat 에만 쓰이고 값이 커서 어차피 건너뛴다.
          if (size === 1) {
            if (buffer.length < 16) return;
            const big = new DataView(buffer.buffer, buffer.byteOffset, 16).getBigUint64(8);
            size = Number(big);
          }
          if (size < 8) {
            broken = true;
            return;
          }

          // moov·moof 만 들여다본다. 나머지(mdat 등)는 크기만큼 건너뛴다.
          if (type !== "moov" && type !== "moof") {
            if (buffer.length >= size) {
              buffer = buffer.subarray(size);
            } else {
              skipRemaining = size - buffer.length;
              buffer = new Uint8Array(0);
              return;
            }
            continue;
          }

          if (buffer.length < size) return; // 이 박스가 다 오면 그때 읽는다
          const box = new DataView(buffer.buffer, buffer.byteOffset, size);

          if (type === "moov" && timescale === null) {
            // `mvhd`(무비) 가 아니라 `mdhd`(트랙) 이다. 실제 파일에서 1000 대 90000 으로
            // 달랐고, `tfdt` 는 후자를 기준으로 한다 — 잘못 고르면 90배 어긋난다.
            const at = findBox(box, "mdhd");
            if (at >= 0) {
              const version = box.getUint8(at);
              // version 0: creation(4) modification(4) timescale(4)
              // version 1: creation(8) modification(8) timescale(4)
              const off = at + 4 + (version === 1 ? 16 : 8);
              if (off + 4 <= size) timescale = box.getUint32(off) || null;
            }
          } else if (type === "moof" && timescale) {
            const at = findBox(box, "tfdt");
            if (at >= 0) {
              const version = box.getUint8(at);
              const base = version === 1 ? Number(box.getBigUint64(at + 4)) : box.getUint32(at + 4);
              mediaSeconds = base / timescale;
            }
          }

          buffer = buffer.subarray(size);
        }
      } catch {
        // 형식이 예상과 다르면 진행률만 포기한다.
        broken = true;
      }
    },
    get seconds(): number | null {
      return broken || timescale === null ? null : mediaSeconds;
    },
  };
}

/**
 * JS 힙에 들고 있는 조각의 상한. 넘으면 Blob 으로 접어 브라우저의 blob 저장소로
 * 넘긴다 — 거기는 필요하면 디스크로 내려가므로 힙에 남는 양이 이 값으로 묶인다.
 */
const BLOB_FOLD_BYTES = 32 * 1024 * 1024;

/** 현장명·CCTV명은 사람이 붙인 값이다. Windows 기준으로 걸러내면 나머지 OS 도 안전하다. */
function sanitizeFilenamePart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** `yyyyMMddHHmmss` → `yyMMddHHmm`. 구간을 분 단위로 고르므로 세기와 초는 뺀다. */
function toFilenameStamp(value: string): string {
  return value.length === 14 ? value.slice(2, 12) : value;
}

/**
 * `시흥거모_2번게이트_2609030900_2609031000.mp4`
 *
 * 서버의 `Content-Disposition`(`cctv_310_20260903090000.mp4`)은 현장·카메라를 알 수 없고
 * 종료 시각도 없어, 여러 건을 폴더에 쌓으면 구분이 안 된다.
 */
function buildDownloadFilename(
  siteName: string | undefined,
  cctvName: string | undefined,
  startDate: string,
  endDate: string
): string {
  const parts = [siteName, cctvName, toFilenameStamp(startDate), toFilenameStamp(endDate)]
    .map((v) => sanitizeFilenamePart(v ?? ""))
    .filter(Boolean);
  return `${parts.join("_")}.mp4`;
}

/**
 * 녹화영상을 받아 파일로 저장한다.
 *
 * 전송을 브라우저에 맡기지 않는 이유는 진행률·취소·에러를 앱 안에서 다루기 위해서다.
 * `<a href>` 는 통짜 이동이라 실패 시점에 우리 JS 가 없다.
 *
 * 대가로 탭을 닫으면 죽고 브라우저 다운로드 목록에도 뜨지 않는다 —
 * `MAX_DOWNLOAD_MINUTES` 가 그만큼 좁은 이유다.
 */
async function downloadRecording(
  cctv: Pick<SafersCCTV, "id" | "name"> & { site?: { name?: string } },
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal; onProgress?: (p: DownloadProgress) => void } = {}
): Promise<void> {
  const { signal, onProgress } = options;
  const url = getDownloadUrl(cctv.id, startDate, endDate);
  const res = await fetch(url, { signal });

  if (!res.ok) throw await ApiError.fromResponse(res);

  if (!res.body) throw new ApiError(500, "응답 본문이 비어 있습니다.");

  const totalSeconds = rangeMinutesOf(startDate, endDate) * 60;
  const clock = createFragmentClock();
  const reader = res.body.getReader();
  const parts: BlobPart[] = [];
  let pending = 0;
  let receivedBytes = 0;
  // 실제 비트레이트로 낸 총 용량. CBR 이라 한 번만 재고 유지한다 — 조각마다 다시
  // 계산하면 화면의 숫자가 계속 흔들린다.
  let projectedBytes: number | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      clock.push(value);
      parts.push(value);
      pending += value.byteLength;
      receivedBytes += value.byteLength;
      // 힙에 쌓인 조각을 접어 blob 저장소로 넘긴다.
      if (pending >= BLOB_FOLD_BYTES) {
        parts.splice(0, parts.length, new Blob(parts));
        pending = 0;
      }
      const seconds = clock.seconds;
      const known = seconds !== null && seconds > 0 && totalSeconds > 0;

      // 미디어 3초면 비트레이트가 잡힌다. 그 전에는 조각 하나의 편차가 커서 못 미덥다.
      if (projectedBytes === null && known && seconds >= 3) {
        projectedBytes = (receivedBytes / seconds) * totalSeconds;
        if (projectedBytes > MAX_DOWNLOAD_BYTES) {
          reader.cancel().catch(() => {});
          throw new ApiError(
            413,
            `이 카메라는 화질이 높아 선택한 구간이 약 ${Math.round(projectedBytes / 1024 / 1024 / 1024)}GB 입니다. 구간을 줄여 다시 받아주세요`
          );
        }
      }

      onProgress?.({
        receivedBytes,
        ratio: known ? Math.min(1, seconds / totalSeconds) : null,
        projectedBytes,
      });
    }
  } catch (err) {
    // 취소는 여기로도 온다. reader 를 풀어 연결을 확실히 닫는다.
    reader.cancel().catch(() => {});
    throw err;
  }

  const blob = new Blob(parts, { type: "video/mp4" });
  const name = buildDownloadFilename(cctv.site?.name, cctv.name, startDate, endDate);

  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 클릭 직후 해제하면 저장이 취소되는 브라우저가 있어 한 틱 미룬다.
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** `yyyyMMddHHmmss` 두 개의 간격(분). 예상 용량 계산에 쓴다. */
function rangeMinutesOf(startDate: string, endDate: string): number {
  const toDate = (v: string) =>
    new Date(
      Number(v.slice(0, 4)),
      Number(v.slice(4, 6)) - 1,
      Number(v.slice(6, 8)),
      Number(v.slice(8, 10)),
      Number(v.slice(10, 12)),
      Number(v.slice(12, 14))
    );
  return (toDate(endDate).getTime() - toDate(startDate).getTime()) / 60000;
}

export const cctvService = {
  ptzMove,
  ptzStop,
  ptzPresets,
  ptzPresetSave,
  ptzPresetGoto,
  ptzPresetDelete,
  getCCTVs,
  syncCCTVs,
  getWHEPUrl,
  requestPlayback,
  downloadRecording,
  estimateDownload,
  MAX_DOWNLOAD_MINUTES,
};
